import { useState, useEffect } from 'react';
import { useTranslation } from '../lib/i18n';
import { useAuthStore } from '../lib/auth';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from 'sonner';
import { 
  Truck,
  TrendingUp,
  Clock,
  Euro,
  MapPin,
  Users,
  Building2,
  Loader2,
  BarChart3,
  PieChart,
  Car,
  Calendar,
  RefreshCw,
  X
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  LineChart,
  Line
} from 'recharts';
import { Money } from '../components/Money';

// Month names in Turkish
const MONTHS = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];

// Colors for charts
const COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];

export default function RoutesReports() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  
  // State
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [firms, setFirms] = useState([]);
  const [chartMetric, setChartMetric] = useState('income'); // 'income', 'hours', 'km', 'count'
  
  // New Analytics States
  const [driverVehicleMatrix, setDriverVehicleMatrix] = useState(null);
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [matrixTimePeriod, setMatrixTimePeriod] = useState('all');
  const [matrixMonth, setMatrixMonth] = useState('all'); // 'all' or 'YYYY-MM'
  const [matrixMetric, setMatrixMetric] = useState('cost'); // 'cost', 'hours', 'km', 'routes'
  const [periodComparison, setPeriodComparison] = useState(null);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [comparisonMode, setComparisonMode] = useState('last6months');
  
  // Filters
  const [selectedCustomer, setSelectedCustomer] = useState('fedex');
  const [selectedFirm, setSelectedFirm] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [customerList, setCustomerList] = useState([]);

  const hasDateRange = !!(dateRange.start && dateRange.end);

  // Fetch customers from DB
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const res = await api.get('/customers');
        if (res.data.success && res.data.data.length > 0) {
          setCustomerList(res.data.data);
          if (!res.data.data.find(c => c.slug === selectedCustomer)) {
            setSelectedCustomer(res.data.data[0].slug);
          }
        }
      } catch (err) { console.error(err); }
    };
    fetchCustomers();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch data
  useEffect(() => {
    fetchReports();
    fetchFirms();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomer, selectedFirm, selectedYear, dateRange]);

  // Fetch driver-vehicle matrix
  useEffect(() => {
    fetchDriverVehicleMatrix();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomer, selectedFirm, matrixTimePeriod, matrixMonth]);

  // Fetch period comparison
  useEffect(() => {
    fetchPeriodComparison();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomer, selectedFirm, comparisonMode]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('customer', selectedCustomer);
      if (hasDateRange) {
        params.append('start_date', dateRange.start);
        params.append('end_date', dateRange.end);
      } else {
        params.append('year', selectedYear);
      }
      if (selectedFirm) params.append('firm_id', selectedFirm);
      
      const response = await api.get(`/routes/reports?${params.toString()}`);
      if (response.data.success) {
        setReportData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching reports:', error);
      toast.error(t('routeReports.loadError') || 'Raporlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const fetchFirms = async () => {
    try {
      const response = await api.get('/firms/');
      if (response.data.success) {
        setFirms(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching firms:', error);
    }
  };

  // Fetch Driver-Vehicle Matrix
  const fetchDriverVehicleMatrix = async () => {
    try {
      setMatrixLoading(true);
      const params = new URLSearchParams();
      params.append('customer', selectedCustomer);
      if (selectedFirm) params.append('firm_id', selectedFirm);
      if (matrixMonth && matrixMonth !== 'all') {
        // Month filter: send start_date and end_date
        const [y, m] = matrixMonth.split('-');
        const startDate = `${y}-${m}-01`;
        const lastDay = new Date(parseInt(y), parseInt(m), 0).getDate();
        const endDate = `${y}-${m}-${String(lastDay).padStart(2, '0')}`;
        params.append('start_date', startDate);
        params.append('end_date', endDate);
      } else if (matrixTimePeriod && matrixTimePeriod !== 'all') {
        params.append('time_period', matrixTimePeriod);
      }
      
      const response = await api.get(`/routes/driver-vehicle-matrix?${params.toString()}`);
      if (response.data.success) {
        setDriverVehicleMatrix(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching driver-vehicle matrix:', error);
    } finally {
      setMatrixLoading(false);
    }
  };

  // Fetch Period Comparison
  const fetchPeriodComparison = async () => {
    try {
      setComparisonLoading(true);
      const params = new URLSearchParams();
      params.append('customer', selectedCustomer);
      if (selectedFirm) params.append('firm_id', selectedFirm);
      params.append('comparison_mode', comparisonMode);
      
      const response = await api.get(`/routes/period-comparison?${params.toString()}`);
      if (response.data.success) {
        setPeriodComparison(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching period comparison:', error);
    } finally {
      setComparisonLoading(false);
    }
  };

  // Month names - will be translated
  const getMonthNames = () => {
    const monthKeys = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
    return monthKeys.map(key => (t(`employeeTracking.months.${key}`) || MONTHS[monthKeys.indexOf(key)]).substring(0, 3));
  };

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('tr-TR', { 
      style: 'currency', 
      currency: 'EUR',
      minimumFractionDigits: 2 
    }).format(value || 0);
  };

  // Format hours
  const formatHours = (hours) => {
    const h = Math.floor(hours || 0);
    const m = Math.round(((hours || 0) - h) * 60);
    return `${h}:${m.toString().padStart(2, '0')}`;
  };

  // Prepare chart data - use consistent keys
  const monthNames = getMonthNames();
  const monthlyChartData = reportData?.monthly?.map((m, idx) => ({
    name: monthNames[idx],
    income: m.total_cost,
    hours: m.total_hours,
    km: m.total_km,
    count: m.route_count
  })) || [];

  // Chart metric configuration
  const chartMetricConfig = {
    income: {
      key: 'income',
      label: t('routeReports.totalAmount') || 'Toplam Tutar',
      color: '#10B981',
      format: (val) => formatCurrency(val),
      icon: '€'
    },
    hours: {
      key: 'hours',
      label: t('routes.workHours') || 'Çalışma Saati',
      color: '#3B82F6',
      format: (val) => formatHours(val),
      icon: '⏱'
    },
    km: {
      key: 'km',
      label: 'Toplam KM',
      color: '#F59E0B',
      format: (val) => `${val?.toLocaleString('tr-TR') || 0} km`,
      icon: '📍'
    },
    count: {
      key: 'count',
      label: t('routeReports.records') || 'Route Sayısı',
      color: '#8B5CF6',
      format: (val) => val?.toString() || '0',
      icon: '📦'
    }
  };

  const currentMetric = chartMetricConfig[chartMetric];

  const driverChartData = reportData?.by_driver?.map(d => ({
    name: d._id || (t('common.unknown') || 'Bilinmeyen'),
    value: d.total_cost,
    cost: d.total_cost,
    hours: d.total_hours,
    km: d.total_km || 0,
    routes: d.route_count
  })) || [];

  const firmChartData = reportData?.by_firm?.map(f => ({
    name: f.firm_name || 'Bilinmeyen',
    value: f.total_cost,
    hours: f.total_hours,
    routes: f.route_count
  })) || [];

  const typeChartData = reportData?.by_type?.map(item => ({
    name: item._id || (t('common.unknown') || 'Bilinmeyen'),
    value: item.count,
    cost: item.total_cost
  })) || [];

  // Year options
  const yearOptions = [];
  const currentYear = new Date().getFullYear();
  for (let y = currentYear; y >= currentYear - 5; y--) {
    yearOptions.push(y);
  }

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-slate-800 p-3 border rounded-lg shadow-lg">
          <p className="font-semibold">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {entry.name}: {entry.name.includes('€') ? formatCurrency(entry.value) : entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6" data-testid="routes-reports-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-emerald-600" />
            {t('routeReports.title') || 'Route Raporları'}
          </h1>
          <p className="text-slate-500 dark:text-slate-400">{t('routeReports.description') || 'Detaylı istatistikler ve analizler'}</p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-end gap-3">
            {/* Customer Select */}
            <div>
              <Label className="text-xs text-slate-500 dark:text-slate-400 mb-2 block">{t('routeReports.customer') || 'Müşteri'}</Label>
              <Select value={selectedCustomer} onValueChange={setSelectedCustomer}>
                <SelectTrigger className="w-[160px] h-9 text-sm" data-testid="report-customer-select">
                  <div className="flex items-center gap-2">
                    {(() => {
                      const c = customerList.find(c => c.slug === selectedCustomer);
                      return c ? (
                        <>
                          <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: c.color }} />
                          <span>{c.name}</span>
                        </>
                      ) : <SelectValue />;
                    })()}
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {customerList.map(cust => (
                    <SelectItem key={cust.slug} value={cust.slug}>
                      <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: cust.color }} />
                        {cust.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Firm Filter */}
            {(user?.role === 'SUPER_ADMIN' || user?.role === 'FULL_ADMIN') && (
              <div>
                <Label className="text-xs text-slate-500 dark:text-slate-400 mb-2 block">{t('routeReports.firm') || 'Firma'}</Label>
                <Select value={selectedFirm || "all"} onValueChange={(v) => setSelectedFirm(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-[130px] h-9 text-sm">
                    <SelectValue placeholder={t('routeReports.allFirms') || 'Tüm Firmalar'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('routeReports.allFirms') || 'Tüm Firmalar'}</SelectItem>
                    {firms.map(firm => (
                      <SelectItem key={firm.id} value={firm.id}>{firm.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Year Filter */}
            <div className={hasDateRange ? 'opacity-40 pointer-events-none' : ''}>
              <Label className="text-xs text-slate-500 dark:text-slate-400 mb-2 block">{t('common.year') || 'Yıl'}</Label>
              <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))} disabled={hasDateRange}>
                <SelectTrigger className="w-[90px] h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {yearOptions.map(year => (
                    <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date Range */}
            <div className={!hasDateRange && (selectedYear) ? '' : ''}>
              <Label className="text-xs text-slate-500 dark:text-slate-400 mb-2 block">{t('common.startDate')}</Label>
              <Input
                type="date"
                className="w-[132px] h-9 text-sm pr-1"
                value={dateRange.start}
                onChange={e => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                data-testid="report-date-start"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500 dark:text-slate-400 mb-2 block">{t('common.endDate')}</Label>
              <Input
                type="date"
                className="w-[132px] h-9 text-sm pr-1"
                value={dateRange.end}
                onChange={e => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                data-testid="report-date-end"
              />
            </div>
            {hasDateRange && (
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 text-slate-400 hover:text-red-500 mb-0"
                onClick={() => setDateRange({ start: '', end: '' })}
                data-testid="report-date-clear"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-emerald-600">{selectedCustomer === 'bpost' ? 'Toplam Start Kost' : (t('routeReports.totalAmount') || 'Toplam Gelir')}</p>
                    <p className="text-2xl font-bold text-emerald-700">
                      <Money amount={reportData?.totals?.total_cost} />
                    </p>
                  </div>
                  <Euro className="h-8 w-8 text-emerald-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-600">{t('routeReports.totalHours') || 'Toplam Saat'}</p>
                    <p className="text-2xl font-bold text-blue-700">
                      {formatHours(reportData?.totals?.total_hours)}
                    </p>
                  </div>
                  <Clock className="h-8 w-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>

            {(selectedCustomer === 'fedex' || selectedCustomer === 'dhl' || selectedCustomer === 'bpost') && (
              <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-orange-600">{t('routeReports.totalKm') || 'Toplam KM'}</p>
                      <p className="text-2xl font-bold text-orange-700">
                        {(reportData?.totals?.total_km || 0).toLocaleString('tr-TR')}
                      </p>
                    </div>
                    <MapPin className="h-8 w-8 text-orange-500" />
                  </div>
                </CardContent>
              </Card>
            )}

            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-purple-600">{t('routeReports.totalRoutes') || 'Toplam Kayıt'}</p>
                    <p className="text-2xl font-bold text-purple-700">
                      {reportData?.totals?.route_count || 0}
                    </p>
                  </div>
                  <Truck className="h-8 w-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Monthly Chart with Toggle */}
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-emerald-600" />
                  {t('routeReports.monthlyTrend') || 'Aylık Trend'} - {selectedYear}
                </CardTitle>
                
                {/* Metric Toggle Buttons */}
                <div className="flex items-center gap-1 p-1 bg-slate-100 dark:bg-slate-800 rounded-lg">
                  {Object.entries(chartMetricConfig).map(([key, config]) => (
                    <button
                      key={key}
                      onClick={() => setChartMetric(key)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                        chartMetric === key
                          ? 'bg-white dark:bg-slate-700 shadow-sm'
                          : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                      }`}
                      style={chartMetric === key ? { color: config.color } : {}}
                    >
                      <span className="mr-1">{config.icon}</span>
                      {config.label}
                    </button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {monthlyChartData.length === 0 || monthlyChartData.every(d => d[currentMetric.key] === 0) ? (
                <div className="h-[350px] flex items-center justify-center text-slate-500 dark:text-slate-400">
                  {t('routeReports.noData') || 'Bu yıl için veri bulunamadı'}
                </div>
              ) : (
                <div className="h-[350px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={monthlyChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis 
                        tickFormatter={(value) => {
                          if (chartMetric === 'income') return `€${(value / 1000).toFixed(0)}k`;
                          if (chartMetric === 'hours') return `${value}h`;
                          if (chartMetric === 'km') return `${(value / 1000).toFixed(0)}k`;
                          return value;
                        }}
                      />
                      <Tooltip 
                        content={({ active, payload, label }) => {
                          if (active && payload && payload.length) {
                            const data = payload[0]?.payload;
                            return (
                              <div className="bg-white dark:bg-slate-800 p-3 rounded-lg shadow-lg border">
                                <p className="font-medium mb-2">{label}</p>
                                <div className="space-y-1 text-sm">
                                  <p style={{ color: currentMetric.color }} className="font-semibold">
                                    {currentMetric.label}: {currentMetric.format(payload[0]?.value)}
                                  </p>
                                  <hr className="my-2 border-slate-200 dark:border-slate-600" />
                                  <p className="text-slate-500 text-xs">
                                    Gelir: {formatCurrency(data?.income)} | 
                                    Saat: {formatHours(data?.hours)} | 
                                    KM: {data?.km?.toLocaleString('tr-TR')}
                                  </p>
                                </div>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Legend 
                        formatter={() => currentMetric.label}
                      />
                      <Bar 
                        dataKey={currentMetric.key}
                        name={currentMetric.label}
                        fill={currentMetric.color}
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Two Column Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Driver Performance */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-blue-600" />
                  {t('routeReports.driverStats') || 'Şoför Performansı'} (Top 10)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {driverChartData.length === 0 ? (
                  <div className="text-center py-12 text-slate-500 dark:text-slate-400">{t('routeReports.noData') || 'Veri bulunamadı'}</div>
                ) : (
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={driverChartData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 12 }} />
                        <Tooltip 
                          formatter={(value, name) => [formatCurrency(value), t('routeReports.totalAmount') || 'Gelir']}
                        />
                        <Bar dataKey="value" fill="#3B82F6" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Firm Distribution (Super Admin) or Route Type Distribution (GeTax) */}
            {(user?.role === 'SUPER_ADMIN' || user?.role === 'FULL_ADMIN') && !selectedFirm && firmChartData.length > 0 ? (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-purple-600" />
                    {t('common.firmDistribution') || 'Firma Dağılımı'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsPieChart>
                        <Pie
                          data={firmChartData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {firmChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                      </RechartsPieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            ) : (selectedCustomer === 'getax' || selectedCustomer === 'express') && typeChartData.length > 0 ? (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PieChart className="h-5 w-5 text-orange-600" />
                    Tur Tipi Dağılımı
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsPieChart>
                        <Pie
                          data={typeChartData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {typeChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </RechartsPieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="h-5 w-5 text-emerald-600" />
                    Aylık Çalışma Saati
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={monthlyChartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="Saat" stroke="#10B981" strokeWidth={2} dot={{ r: 4 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Driver Details Table */}
          {driverChartData.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>{t('routeReports.driverDetails') || 'Şoför Detayları'}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4">{t('routeReports.driver') || 'Şoför'}</th>
                        <th className="text-right py-3 px-4">{t('routeReports.routeCount') || 'Route Sayısı'}</th>
                        <th className="text-right py-3 px-4">{t('routeReports.totalHours') || 'Toplam Saat'}</th>
                        {(selectedCustomer === 'fedex' || selectedCustomer === 'dhl' || selectedCustomer === 'bpost') && (
                          <th className="text-right py-3 px-4">{t('routeReports.totalKm') || 'Toplam KM'}</th>
                        )}
                        <th className="text-right py-3 px-4">{t('routeReports.totalAmount') || 'Toplam Tutar'}</th>
                        <th className="text-right py-3 px-4">{t('routeReports.avgPerRoute') || 'Ortalama/Route'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {driverChartData.map((driver, index) => (
                        <tr key={index} className="border-b hover:bg-slate-50 dark:hover:bg-slate-800">
                          <td className="py-3 px-4 font-medium">{driver.name}</td>
                          <td className="text-right py-3 px-4">{driver.routes}</td>
                          <td className="text-right py-3 px-4">{formatHours(driver.hours)}</td>
                          {(selectedCustomer === 'fedex' || selectedCustomer === 'dhl' || selectedCustomer === 'bpost') && (
                            <td className="text-right py-3 px-4">{(driver.km || 0).toLocaleString('tr-TR')}</td>
                          )}
                          <td className="text-right py-3 px-4 font-medium text-green-600"><Money amount={driver.cost} revealKey={`rep-drv-cost-${driver.name || driver.id || ''}`} /></td>
                          <td className="text-right py-3 px-4 text-slate-500"><Money amount={driver.cost / (driver.routes || 1)} revealKey={`rep-drv-avg-${driver.name || driver.id || ''}`} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Period Comparison Card */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-indigo-600" />
                  {t('routeReports.periodComparison') || 'Dönem Karşılaştırması'}
                </CardTitle>
                <div className="flex gap-1">
                  <Button
                    variant={comparisonMode === 'last6months' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setComparisonMode('last6months')}
                    className="text-xs"
                  >
                    {t('routeReports.last6Months') || 'Son 6 Ay vs Önceki'}
                  </Button>
                  <Button
                    variant={comparisonMode === 'last12months' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setComparisonMode('last12months')}
                    className="text-xs"
                  >
                    {t('routeReports.last12Months') || 'Son 12 Ay vs Önceki'}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {comparisonLoading ? (
                <div className="h-[200px] flex items-center justify-center">
                  <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
                </div>
              ) : periodComparison ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    {/* Current Period */}
                    <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg border border-green-200 dark:border-green-800">
                      <h4 className="font-medium text-sm text-green-700 dark:text-green-400 mb-3">
                        {comparisonMode === 'last6months' 
                          ? t('routeReports.last6MonthsLabel') || periodComparison.current?.label || 'Son 6 Ay'
                          : t('routeReports.last12MonthsLabel') || periodComparison.current?.label || 'Son 12 Ay'}
                      </h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <p className="text-xs text-slate-500">{t('routeReports.totalAmount') || 'Gelir'}</p>
                          <p className="font-bold text-lg text-green-600"><Money amount={periodComparison.current?.total_cost || 0} /></p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">{t('routeReports.totalHours') || 'Saat'}</p>
                          <p className="font-bold text-lg">{formatHours(periodComparison.current?.total_hours || 0)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">{t('routeReports.totalKm') || 'KM'}</p>
                          <p className="font-bold">{(periodComparison.current?.total_km || 0).toLocaleString('tr-TR')}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">{t('routeReports.totalRoutes') || 'Route'}</p>
                          <p className="font-bold">{periodComparison.current?.route_count || 0}</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Previous Period */}
                    <div className="p-4 bg-gradient-to-r from-slate-50 to-gray-50 dark:from-slate-800/50 dark:to-gray-800/50 rounded-lg border border-slate-200 dark:border-slate-700">
                      <h4 className="font-medium text-sm text-slate-600 dark:text-slate-400 mb-3">
                        {comparisonMode === 'last6months' 
                          ? t('routeReports.previous6MonthsLabel') || periodComparison.previous?.label || 'Önceki 6 Ay'
                          : t('routeReports.previous12MonthsLabel') || periodComparison.previous?.label || 'Önceki 12 Ay'}
                      </h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <p className="text-xs text-slate-500">{t('routeReports.totalAmount') || 'Gelir'}</p>
                          <p className="font-bold text-lg"><Money amount={periodComparison.previous?.total_cost || 0} /></p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">{t('routeReports.totalHours') || 'Saat'}</p>
                          <p className="font-bold text-lg">{formatHours(periodComparison.previous?.total_hours || 0)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">{t('routeReports.totalKm') || 'KM'}</p>
                          <p className="font-bold">{(periodComparison.previous?.total_km || 0).toLocaleString('tr-TR')}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">{t('routeReports.totalRoutes') || 'Route'}</p>
                          <p className="font-bold">{periodComparison.previous?.route_count || 0}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Growth Badges */}
                  <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg">
                    <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
                      <TrendingUp className="h-4 w-4" />
                      {t('routeReports.growthSummary') || 'Büyüme Özeti'}
                    </h4>
                    <div className="flex flex-wrap gap-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-slate-600">{t('routeReports.totalAmount') || 'Gelir'}:</span>
                        <Badge 
                          variant={periodComparison.growth?.cost_percent >= 0 ? 'default' : 'destructive'}
                          className={periodComparison.growth?.cost_percent >= 0 ? 'bg-green-100 text-green-700' : ''}
                        >
                          {periodComparison.growth?.cost_percent >= 0 ? '+' : ''}{periodComparison.growth?.cost_percent}%
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-slate-600">{t('routeReports.totalHours') || 'Saat'}:</span>
                        <Badge 
                          variant={periodComparison.growth?.hours_percent >= 0 ? 'default' : 'destructive'}
                          className={periodComparison.growth?.hours_percent >= 0 ? 'bg-blue-100 text-blue-700' : ''}
                        >
                          {periodComparison.growth?.hours_percent >= 0 ? '+' : ''}{periodComparison.growth?.hours_percent}%
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-slate-600">KM:</span>
                        <Badge 
                          variant={periodComparison.growth?.km_percent >= 0 ? 'default' : 'destructive'}
                          className={periodComparison.growth?.km_percent >= 0 ? 'bg-orange-100 text-orange-700' : ''}
                        >
                          {periodComparison.growth?.km_percent >= 0 ? '+' : ''}{periodComparison.growth?.km_percent}%
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-slate-600">Route:</span>
                        <Badge 
                          variant={periodComparison.growth?.routes_percent >= 0 ? 'default' : 'destructive'}
                          className={periodComparison.growth?.routes_percent >= 0 ? 'bg-purple-100 text-purple-700' : ''}
                        >
                          {periodComparison.growth?.routes_percent >= 0 ? '+' : ''}{periodComparison.growth?.routes_percent}%
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="h-[200px] flex items-center justify-center text-slate-400">
                  {t('common.noData') || 'Veri bulunamadı'}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Driver-Vehicle Performance Matrix */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Car className="h-5 w-5 text-amber-600" />
                  {t('routeReports.driverVehicleMatrix') || 'Şoför-Araç Performans Matrisi'}
                </CardTitle>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2 mt-2">
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {t('routeReports.driverVehicleMatrixDesc') || 'Her şoförün hangi araçla ne kadar performans gösterdiğini görün'}
                </p>
                <div className="flex items-center gap-2">
                  {/* Month Filter */}
                  <Select value={matrixMonth} onValueChange={(v) => {
                    setMatrixMonth(v);
                    if (v !== 'all') setMatrixTimePeriod('custom');
                    else setMatrixTimePeriod('all');
                  }}>
                    <SelectTrigger className="w-[140px] h-7 text-xs">
                      <SelectValue placeholder={t('routes.selectMonth') || 'Select Month'} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('routes.allTime') || 'All Time'}</SelectItem>
                      {(() => {
                        const options = [];
                        const now = new Date();
                        for (let i = 0; i < 12; i++) {
                          const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
                          const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                          const label = d.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' });
                          options.push(<SelectItem key={val} value={val}>{label}</SelectItem>);
                        }
                        return options;
                      })()}
                    </SelectContent>
                  </Select>

                  <div className="flex gap-1">
                    <Button
                      variant={matrixTimePeriod === 'daily' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => { setMatrixTimePeriod('daily'); setMatrixMonth('all'); }}
                      className="text-xs h-7"
                    >
                      {t('packages.last7Days') || 'Son 7 Gün'}
                    </Button>
                    <Button
                      variant={matrixTimePeriod === 'weekly' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => { setMatrixTimePeriod('weekly'); setMatrixMonth('all'); }}
                      className="text-xs h-7"
                    >
                      {t('packages.last4Weeks') || 'Son 4 Hafta'}
                    </Button>
                    <Button
                      variant={matrixTimePeriod === 'monthly' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => { setMatrixTimePeriod('monthly'); setMatrixMonth('all'); }}
                      className="text-xs h-7"
                    >
                      {t('packages.last3Months') || 'Son 3 Ay'}
                    </Button>
                    <Button
                      variant={matrixTimePeriod === 'all' && matrixMonth === 'all' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => { setMatrixTimePeriod('all'); setMatrixMonth('all'); }}
                      className="text-xs h-7"
                    >
                      {t('packages.allTime') || 'Tüm Zamanlar'}
                    </Button>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {matrixLoading ? (
                <div className="h-[300px] flex items-center justify-center">
                  <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
                </div>
              ) : driverVehicleMatrix?.matrix?.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="sticky left-0 bg-white dark:bg-slate-900 z-10 min-w-[150px]">
                          {t('routeReports.driver') || 'Şoför'}
                        </TableHead>
                        {driverVehicleMatrix.vehicles?.map(vehicle => (
                          <TableHead key={vehicle} className="text-center min-w-[100px]">
                            <Badge variant="outline" className="text-xs">
                              {vehicle}
                            </Badge>
                          </TableHead>
                        ))}
                        <TableHead className="text-right min-w-[100px] font-bold bg-slate-50 dark:bg-slate-800">
                          {t('routeReports.totalAmount') || 'Toplam Tutar'}
                        </TableHead>
                        <TableHead className="text-right min-w-[80px] font-bold bg-slate-50 dark:bg-slate-800">
                          {t('routeReports.totalHours') || 'Toplam Saat'}
                        </TableHead>
                        <TableHead className="text-right min-w-[60px] font-bold bg-slate-50 dark:bg-slate-800">
                          Route
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {driverVehicleMatrix.matrix.slice(0, 15).map((driver, idx) => (
                        <TableRow key={idx} className={idx < 3 ? 'bg-green-50/30 dark:bg-green-900/10' : ''}>
                          <TableCell className="sticky left-0 bg-white dark:bg-slate-900 font-medium">
                            <div className="flex items-center gap-2">
                              {idx < 3 && (
                                <Badge variant={idx === 0 ? 'default' : 'secondary'} className="text-xs w-5 h-5 p-0 flex items-center justify-center">
                                  {idx + 1}
                                </Badge>
                              )}
                              <span className="truncate max-w-[120px]">{driver.driver}</span>
                            </div>
                          </TableCell>
                          {driverVehicleMatrix.vehicles?.map(vehicle => {
                            const vehicleData = driver.vehicles?.[vehicle];
                            const hasCost = vehicleData?.cost;
                            const hasHours = vehicleData?.hours;
                            const hasRoutes = vehicleData?.routes;
                            const hasData = hasCost || hasHours || hasRoutes;
                            const maxCost = Math.max(...driverVehicleMatrix.matrix.map(d => d.totals?.total_cost || 0));
                            const intensity = hasCost ? Math.min(hasCost / (maxCost * 0.3), 1) : 0;
                            
                            return (
                              <TableCell 
                                key={vehicle} 
                                className="text-center p-1"
                                style={{
                                  backgroundColor: hasData 
                                    ? `rgba(245, 158, 11, ${intensity * 0.3})` 
                                    : 'transparent'
                                }}
                              >
                                {hasData ? (
                                  <div className="flex flex-col gap-0 text-xs leading-tight">
                                    {hasCost ? <span className="text-green-700 dark:text-green-400 font-medium">{formatCurrency(hasCost)}</span> : null}
                                    {hasHours ? <span className="text-blue-600 dark:text-blue-400">{formatHours(hasHours)}</span> : null}
                                    {hasRoutes ? <span className="text-slate-500">{hasRoutes}r</span> : null}
                                  </div>
                                ) : '-'}
                              </TableCell>
                            );
                          })}
                          <TableCell className="text-right font-bold bg-slate-50 dark:bg-slate-800 text-green-700 dark:text-green-400">
                            {formatCurrency(driver.totals?.total_cost || 0)}
                          </TableCell>
                          <TableCell className="text-right font-bold bg-slate-50 dark:bg-slate-800 text-blue-600 dark:text-blue-400">
                            {formatHours(driver.totals?.total_hours || 0)}
                          </TableCell>
                          <TableCell className="text-right font-bold bg-slate-50 dark:bg-slate-800">
                            {driver.totals?.route_count || 0}
                          </TableCell>
                        </TableRow>
                      ))}
                      {/* Vehicle Totals Row */}
                      <TableRow className="bg-slate-100 dark:bg-slate-700 font-bold">
                        <TableCell className="sticky left-0 bg-slate-100 dark:bg-slate-700">
                          {t('routeReports.vehicleTotal') || 'Araç Toplamı'}
                        </TableCell>
                        {driverVehicleMatrix.vehicles?.map(vehicle => (
                          <TableCell key={vehicle} className="text-center p-1">
                            <div className="flex flex-col gap-0 text-xs leading-tight font-bold">
                              <span className="text-green-700 dark:text-green-400">{formatCurrency(driverVehicleMatrix.vehicle_totals?.[vehicle]?.cost || 0)}</span>
                              <span className="text-blue-600 dark:text-blue-400">{formatHours(driverVehicleMatrix.vehicle_totals?.[vehicle]?.hours || 0)}</span>
                              <span className="text-slate-600">{driverVehicleMatrix.vehicle_totals?.[vehicle]?.routes || 0}r</span>
                            </div>
                          </TableCell>
                        ))}
                        <TableCell className="text-right bg-slate-200 dark:bg-slate-600 text-green-700 dark:text-green-400 font-bold">
                          {formatCurrency(driverVehicleMatrix.matrix.reduce((sum, d) => sum + (d.totals?.total_cost || 0), 0))}
                        </TableCell>
                        <TableCell className="text-right bg-slate-200 dark:bg-slate-600 text-blue-600 dark:text-blue-400 font-bold">
                          {formatHours(driverVehicleMatrix.matrix.reduce((sum, d) => sum + (d.totals?.total_hours || 0), 0))}
                        </TableCell>
                        <TableCell className="text-right bg-slate-200 dark:bg-slate-600 font-bold">
                          {driverVehicleMatrix.matrix.reduce((sum, d) => sum + (d.totals?.route_count || 0), 0)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                  {driverVehicleMatrix.driver_count > 15 && (
                    <p className="text-xs text-slate-500 mt-2 text-center">
                      {t('routeReports.showingTopDrivers') || 'En yüksek performanslı'} 15 / {driverVehicleMatrix.driver_count} {t('routeReports.drivers') || 'şoför gösteriliyor'}
                    </p>
                  )}
                </div>
              ) : (
                <div className="h-[200px] flex items-center justify-center text-slate-400">
                  {t('common.noData') || 'Veri bulunamadı'}
                </div>
              )}
            </CardContent>
          </Card>

        </>
      )}
    </div>
  );
}
